import { blockComponent, type FormPage } from '@ayumia/component-runtime';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { CanvasNode, CanvasRenderProps, LibraryEntry, NodeActions } from '../../editor/canvas-types';
import { boxOf, sameBox, type Box } from '../../editor/dnd/geometry';
import { ICONS, InteractionLayer, type FrameSpec, type MenuAction, type ToolbarAction } from '../../editor/dnd/InteractionLayer';
import { pickedAnnouncement, slotAnnouncement, startKeyboardDrag, stepKeyboardDrag, type KeyboardDrag } from '../../editor/dnd/keyboard-drag';
import { armPointerDrag } from '../../editor/dnd/pointer-drag';
import { getAt, setAt } from '../../editor/json-pointer';
import { blocksOf, declaredPages, type Template } from '../layout-model';
import { blockOf, componentByName, type EditorRegistry } from '../layout-registry';
import { cssEscape, dropAt, editableAt, elementFor, hiddenTargets, labelElementFor, layersDropAt, lineForTarget, navigationAt, selectionAt, visibleZones, type CanvasContext } from './canvas-dom';
import { addBlankPage, dropSlots, kindOf, labelText, performDrop, renameLabel, targetFits, type DragKind, type DragSource, type DropResult, type DropSlot, type LabelPart } from './drop-targets';

/**
 * Canevas en mode édition (ADR-146 §5.3, addendum (13)) : le rendu réel de la fiche ne reçoit plus aucun
 * geste (ni focus, ni saisie, ni clic) ; le canevas les lit et les traduit en opérations du modèle, par
 * l'historique de l'éditeur (annulables par Ctrl Z), sous la recette :
 * - survol (contour fin, étiquette), clic (sélection synchronisée avec les calques et l'inspecteur), Maj+clic
 *   (sélection multiple), Échap (parent) ;
 * - barre flottante (parent, monter, descendre, déplacer, dupliquer, supprimer, « ⋯ ») ;
 * - glisser-déposer depuis la bibliothèque, sur le canevas et dans les calques, au pointeur ou au clavier ;
 * - onglets, étapes, « Suivant » : navigation d'aperçu, sans saisie ; pages ajoutées, renommées, réordonnées,
 *   supprimées depuis la barre d'onglets ;
 * - double-clic : libellé modifié sur place (Entrée valide, Échap annule).
 */

interface DragState {
  source: DragSource;
  name: string;
  kind: DragKind;
  category: string;
  mode: 'pointer' | 'keyboard';
  point?: { x: number; y: number } | undefined;
  target: string | null;
  line: Box | null;
  refused: string | null;
  zones: Array<{ region: string; box: Box; label: string }>;
  dock: Array<{ target: string; label: string }>;
  keyboard?: { state: KeyboardDrag; slots: DropSlot[] } | undefined;
}

/** Redimensionnement en cours d'un champ : largeur sur la grille de 12 colonnes (contrat 1.10.0). */
interface ResizeState {
  id: string;
  pointer: string;
  side: 'e' | 'w';
  startX: number;
  /** Largeur au départ du geste, en colonnes. */
  from: number;
  /** Largeur montrée, en colonnes. */
  columns: number;
  /** Boîte de la grille de la partie (repères des colonnes). */
  grid: Box;
  element: HTMLElement;
  /** Style d'origine de l'élément, rendu à la fin du geste. */
  saved: string;
}

/** Largeur déclarée d'un champ en colonnes (absente : toute la largeur, 12). */
export function fieldColumns(template: Template, pointer: string): number {
  const entry = getAt(template, pointer) as { presentation?: { layout?: { width?: number; span?: string } } } | undefined;
  return entry?.presentation?.layout?.width ?? 12;
}

/** Pose la largeur d'un champ (12 colonnes : la largeur par défaut, la clé disparaît). */
export function setFieldColumns(template: Template, pointer: string, columns: number): Template {
  const entry = getAt(template, pointer) as Record<string, unknown> | undefined;
  if (entry === undefined) return template;
  const presentation = { ...((entry['presentation'] ?? {}) as Record<string, unknown>) };
  const layout = { ...((presentation['layout'] ?? {}) as Record<string, unknown>) };
  if (columns >= 12) delete layout['width'];
  else layout['width'] = Math.max(1, Math.round(columns));
  if (Object.keys(layout).length === 0) delete presentation['layout'];
  else presentation['layout'] = layout;
  const next = { ...entry };
  if (Object.keys(presentation).length === 0) delete next['presentation'];
  else next['presentation'] = presentation;
  return setAt(template, pointer, next);
}

/** Un champ d'une partie simple se redimensionne ; un champ d'une liste reste dans sa colonne. */
const RESIZABLE = /^field:\/sections\/\d+\/fields\/\d+$/;

interface EditState {
  id: string;
  part: LabelPart;
  box: Box;
  fontSize?: number;
}

const flatten = (node: CanvasNode, into = new Map<string, CanvasNode>()): Map<string, CanvasNode> => {
  into.set(node.id, node);
  for (const child of node.children) flatten(child, into);
  return into;
};

const MOVABLE = /^(block|section|field|page):/;

export function CanvasEditing({ registry, props, tree, actionsOf, library, pages, page, onNavigate, children }: {
  registry: EditorRegistry;
  props: CanvasRenderProps<Template>;
  tree: (template: Template) => CanvasNode;
  actionsOf: (template: Template, id: string) => NodeActions<Template>;
  library: (template: Template) => readonly LibraryEntry<Template>[];
  /** Pages visibles de l'aperçu et page montrée. */
  pages: readonly FormPage[];
  page: string | undefined;
  /** Montre une page (onglet, étape, « Suivant ») et la choisit dans les calques. */
  onNavigate: (pageId: string) => void;
  children: ReactNode;
}) {
  const { document: template, selected, onSelect, trying, zones, apply, onRefused, onAdd } = props;
  const host = useRef<HTMLDivElement>(null);
  const sink = useRef<HTMLDivElement>(null);
  const [root, setRoot] = useState<Element | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const [extras, setExtras] = useState<string[]>([]);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [resize, setResize] = useState<ResizeState | null>(null);
  const [live, setLive] = useState('');
  const [boxes, setBoxes] = useState<Record<string, Box>>({});
  const [, setTick] = useState(0);
  const editable = apply !== undefined && !trying;
  const nodes = useMemo(() => flatten(tree(template)), [tree, template]);
  const declared = declaredPages(template).length > 0;
  const context = (): CanvasContext | null => (host.current === null ? null : { template, registry, host: host.current, page, pages });
  const say = (message: string): void => setLive((current) => (current === message ? `${message} ` : message));
  const nameOf = (id: string): string => nodes.get(id)?.label ?? 'Élément';

  // Une sélection qui disparaît (suppression, annulation) sort de la sélection multiple.
  const shownExtras = extras.filter((id) => id !== selected && nodes.has(id));

  // ---- Sources glissées.
  const sourceInfo = (source: DragSource): { name: string; kind: DragKind; category: string } | null => {
    const kind = kindOf(source);
    if (kind === null) return null;
    let category: string = kind;
    if (kind === 'block') {
      const name = source.kind === 'library' ? source.entry.id.slice(6) : blockComponent(blocksOf(template).find((block) => block.block_id === source.id.slice(6)) ?? { component: '' }).name;
      category = blockOf(componentByName(registry, name))?.category ?? 'display';
    }
    return { name: source.kind === 'library' ? source.entry.label : nameOf(source.id), kind, category };
  };
  const results = useRef<{ template: Template; source: DragSource | null; map: Map<string, DropResult> }>({ template, source: null, map: new Map() });
  const resultOf = (source: DragSource, target: string): DropResult => {
    const cache = results.current;
    if (cache.template !== template || cache.source !== source) results.current = { template, source, map: new Map() };
    const map = results.current.map;
    if (!map.has(target)) map.set(target, performDrop(template, registry, source, target));
    return map.get(target)!;
  };

  const finish = (source: DragSource, target: string | null, where?: string): void => {
    setDrag(null);
    document.body.classList.remove('ay-dnd-dragging');
    const info = sourceInfo(source);
    if (target === null || info === null || apply === undefined) {
      say('Déplacement annulé.');
      return;
    }
    const result = resultOf(source, target);
    if (result === null) {
      say(`« ${info.name} » est déjà à cette place.`);
      return;
    }
    if ('refused' in result) {
      onRefused?.(result.refused);
      say(`Refusé : ${result.refused}`);
      return;
    }
    apply(result.document);
    setExtras([]);
    onSelect(result.id);
    say(`« ${info.name} » ${source.kind === 'library' ? 'ajouté' : 'déplacé'}${where === undefined ? '' : ` : ${where}`}. Ctrl Z pour annuler.`);
    // Le focus revient à l'élément déposé dans les calques, s'il y est.
    window.setTimeout(() => {
      const row = root?.querySelector<HTMLElement>(`.ay-studio-layers [data-node="${cssEscape(result.id)}"] [data-layer]`);
      row?.focus();
    }, 0);
  };

  // ---- Glisser au pointeur.
  const begin = (source: DragSource, start: { clientX: number; clientY: number; pointerId?: number; button?: number }): void => {
    if (!editable) return;
    const info = sourceInfo(source);
    if (info === null) return;
    let last: { target: string | null; where?: string } = { target: null };
    let docked: Array<{ target: string; label: string }> = [];
    armPointerDrag(start, {
      onStart: () => {
        const current = context();
        const shown = current === null ? [] : visibleZones(current, info.kind === 'section' ? 'section' : info.category);
        const dock = current === null ? [] : hiddenTargets(current, info.kind, info.kind === 'section' ? 'section' : info.category, new Set(shown.map((zone) => zone.region)));
        docked = dock;
        document.body.classList.add('ay-dnd-dragging');
        setHover(null);
        setDrag({ source, ...info, mode: 'pointer', target: null, line: null, refused: null, zones: info.kind === 'block' || info.kind === 'section' ? shown : [], dock });
        say(`« ${info.name} » en main. Montrez sa place ; Échap pour annuler.`);
      },
      onMove: (x, y) => {
        const current = context();
        const hit = typeof document.elementFromPoint === 'function' ? document.elementFromPoint(x, y) : null;
        const dockTarget = hit?.closest('[data-drop-target]')?.getAttribute('data-drop-target') ?? null;
        let point: { target: string; line: Box | null } | null = dockTarget === null ? null : { target: dockTarget, line: null };
        if (point === null && current !== null) point = dropAt(current, info.kind, hit, x, y) ?? layersDropAt(template, info.kind, hit, x, y);
        if (point !== null && !targetFits(info.kind, point.target)) point = null;
        const result = point === null ? null : resultOf(source, point.target);
        const refused = result !== null && 'refused' in result ? result.refused : null;
        const target = point === null || result === null ? null : point.target;
        const where = dockTarget === null ? undefined : docked.find((entry) => entry.target === dockTarget)?.label;
        last = { target: refused === null ? target : null, ...(where === undefined ? {} : { where }) };
        setDrag((state) => (state === null ? state : { ...state, point: { x, y }, target, line: target === null ? null : point!.line, refused }));
      },
      onDrop: () => finish(source, last.target, last.where),
      onCancel: () => {
        setDrag(null);
        document.body.classList.remove('ay-dnd-dragging');
        say('Déplacement annulé.');
      },
    });
  };

  // ---- Glisser au clavier.
  const startKeyboard = (source: DragSource, focusSink: boolean): void => {
    if (!editable) return;
    const info = sourceInfo(source);
    if (info === null) return;
    const { slots, start } = dropSlots(template, registry, source);
    const state = startKeyboardDrag(info.name, slots, start);
    setHover(null);
    setDrag({ source, ...info, mode: 'keyboard', target: null, line: null, refused: null, zones: [], dock: [], keyboard: { state, slots } });
    say(pickedAnnouncement(state));
    if (focusSink) window.setTimeout(() => sink.current?.focus(), 0);
  };
  const keyboardKey = (key: string): boolean => {
    if (drag?.keyboard === undefined) return false;
    const { state, slots } = drag.keyboard;
    const step = stepKeyboardDrag(state, key);
    if (step.action === 'none') return [' ', 'Enter', 'Spacebar', 'Tab'].includes(key);
    if (step.action === 'cancel') {
      setDrag(null);
      say(`Déplacement annulé : « ${state.name} » reste à sa place.`);
      return true;
    }
    if (step.action === 'drop') {
      const slot = slots[state.index];
      const result = slot === undefined ? null : resultOf(drag.source, slot.target);
      if (result !== null && 'refused' in result) {
        say(`Refusé : ${result.refused} Choisissez une autre place, ou Échap pour annuler.`);
        return true;
      }
      finish(drag.source, slot?.target ?? null, slot?.label);
      return true;
    }
    const slot = slots[step.state.index]!;
    const result = resultOf(drag.source, slot.target);
    const refused = result !== null && 'refused' in result ? result.refused : null;
    if (slot.page !== undefined && slot.page !== page && declared) onNavigate(slot.page);
    setDrag({ ...drag, keyboard: { state: step.state, slots }, target: slot.target, refused, line: null });
    say(slotAnnouncement(step.state, refused));
    return true;
  };

  // ---- Sélection, navigation, multi-sélection.
  const toggleExtra = (id: string): void => {
    if (id === 'form' || !nodes.has(id) || nodes.get(id)?.heading === true) return;
    if (selected === '' || selected === id) {
      onSelect(id);
      return;
    }
    const next = shownExtras.includes(id) ? shownExtras.filter((candidate) => candidate !== id) : [...shownExtras, id];
    setExtras(next);
    say(`${next.length + 1} éléments choisis.`);
  };
  // Le rendu ne prend jamais le focus en mode édition : la scène le prend, pour que Suppr, Échap, Ctrl Z et
  // les flèches s'appliquent à l'élément choisi au canevas.
  const focusStage = (): void => {
    const stage = host.current?.closest<HTMLElement>('.ay-studio__stage');
    if (stage !== null && stage !== undefined && document.activeElement !== stage) stage.focus({ preventScroll: true });
  };
  const choose = (target: Element, shift: boolean): void => {
    const current = context();
    if (current === null) return;
    focusStage();
    const pageId = navigationAt(current, target);
    if (pageId !== null && !shift) {
      setExtras([]);
      onNavigate(pageId);
      const label = pages.find((candidate) => candidate.page_id === pageId)?.label as Record<string, string> | undefined;
      say(`Page « ${label?.['fr'] ?? ''} » montrée.`);
      return;
    }
    const id = selectionAt(current, target);
    if (shift) toggleExtra(id);
    else {
      setExtras([]);
      onSelect(id);
    }
  };
  const startEdit = (id: string, part: LabelPart, element: Element): void => {
    if (!editable || host.current === null) return;
    const width = host.current.offsetWidth;
    const scale = width > 0 ? host.current.getBoundingClientRect().width / width : 1;
    const size = typeof getComputedStyle === 'function' ? Number.parseFloat(getComputedStyle(element).fontSize) : Number.NaN;
    if (id !== selected) onSelect(id);
    setHover(null);
    setEdit({ id, part, box: boxOf(element), ...(Number.isFinite(size) ? { fontSize: Math.max(13, Math.round(size * scale)) } : {}) });
  };
  const rename = (id: string): void => {
    const current = context();
    const label = current === null ? null : labelElementFor(current, id);
    if (label !== null) startEdit(id, label.part, label.element);
  };

  // Dupliquer un champ ouvre son libellé en édition sur place, avec la proposition « (copie) » sélectionnée.
  const renameRef = useRef(rename);
  renameRef.current = rename;
  useEffect(() => {
    const onRename = (event: Event): void => {
      const id = (event as CustomEvent<string>).detail;
      if (typeof id === 'string' && id.startsWith('field:')) window.setTimeout(() => renameRef.current(id), 0);
    };
    document.addEventListener('ay-studio:rename', onRename);
    return () => document.removeEventListener('ay-studio:rename', onRename);
  }, []);

  // ---- Redimensionner : poignées réelles, repères de colonnes, aperçu direct, Échap annule, Ctrl Z défait.
  const startResize = (side: 'e' | 'w', event: { clientX: number; preventDefault: () => void; stopPropagation: () => void }): void => {
    const current = context();
    if (current === null || !editable || !RESIZABLE.test(selected)) return;
    const element = elementFor(current, selected) as HTMLElement | null;
    const grid = element?.parentElement;
    if (element === null || element === undefined || grid === null || grid === undefined) return;
    event.preventDefault();
    event.stopPropagation();
    const pointer = selected.slice(6);
    const from = fieldColumns(template, pointer);
    setResize({ id: selected, pointer, side, startX: event.clientX, from, columns: from, grid: boxOf(grid), element, saved: element.getAttribute('style') ?? '' });
  };
  useEffect(() => {
    if (resize === null) return undefined;
    const step = resize.grid.width / 12;
    const columnsAt = (clientX: number): number => {
      const delta = (clientX - resize.startX) * (resize.side === 'e' ? 1 : -1);
      return Math.max(1, Math.min(12, Math.round(resize.from + delta / Math.max(1, step))));
    };
    const preview = (columns: number): void => {
      resize.element.style.maxWidth = `${(columns / 12) * 100}%`;
      if (resize.side === 'w') resize.element.style.justifySelf = 'end';
    };
    const restore = (): void => {
      if (resize.saved === '') resize.element.removeAttribute('style');
      else resize.element.setAttribute('style', resize.saved);
    };
    const onMove = (event: PointerEvent): void => {
      const columns = columnsAt(event.clientX);
      preview(columns);
      setResize((state) => (state === null || state.columns === columns ? state : { ...state, columns }));
    };
    const onUp = (event: PointerEvent): void => {
      event.stopPropagation();
      const columns = columnsAt(event.clientX);
      restore();
      setResize(null);
      if (columns !== resize.from && apply !== undefined) {
        apply(setFieldColumns(template, resize.pointer, columns));
        say(`Largeur : ${columns}/12. Ctrl Z pour annuler.`);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      restore();
      setResize(null);
      say('Redimensionnement annulé.');
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [resize, template, apply]);

  // Largeurs déclarées montrées dans l'aperçu d'édition, tant que le rendu de la fiche ne les applique pas lui-même
  // (couloir de rendu) : un style posé ici seulement, retiré dès qu'il ne sert plus. Le rendu réel n'est pas modifié.
  const sized = useRef(new Map<HTMLElement, string>());
  useLayoutEffect(() => {
    const current = context();
    const wanted = new Map<HTMLElement, number>();
    if (current !== null && !trying) {
      template.sections.forEach((section, s) => {
        if (section.repeat !== undefined || section.series !== undefined) return;
        section.fields.forEach((_, f) => {
          const pointer = `/sections/${s}/fields/${f}`;
          const columns = fieldColumns(template, pointer);
          if (columns >= 12) return;
          const element = elementFor(current, `field:${pointer}`) as HTMLElement | null;
          if (element !== null) wanted.set(element, columns);
        });
      });
    }
    for (const [element, saved] of sized.current) {
      if (wanted.has(element) || (resize !== null && element === resize.element)) continue;
      if (saved === '') element.removeAttribute('style');
      else element.setAttribute('style', saved);
      sized.current.delete(element);
    }
    for (const [element, columns] of wanted) {
      if (resize !== null && element === resize.element) continue;
      if (!sized.current.has(element)) sized.current.set(element, element.getAttribute('style') ?? '');
      element.style.maxWidth = `${(columns / 12) * 100}%`;
    }
  });

  // ---- Écouteurs : le rendu réel ne reçoit rien en mode édition.
  const latest = useRef({ choose, begin, startEdit, editable, trying, edit, drag });
  latest.current = { choose, begin, startEdit, editable, trying, edit, drag };
  const handled = useRef(false);
  useEffect(() => {
    const element = host.current;
    if (element === null) return undefined;
    setRoot(element.closest('.ay-studio') ?? document.body);
    const off = (): boolean => latest.current.trying;
    const stop = (event: Event): void => {
      if (off()) return;
      event.stopPropagation();
      if (event.cancelable) event.preventDefault();
    };
    const onPointerDown = (event: PointerEvent): void => {
      if (off()) return;
      event.stopPropagation();
      handled.current = false;
      if ((event.button ?? 0) !== 0 || latest.current.edit !== null) return;
      const current = latest.current;
      const target = event.target as Element;
      const context0 = { template, registry, host: element, page, pages };
      const pageId = target.closest('.ay-tabs__tab, .ay-staged__step') !== null ? navigationAt(context0, target) : null;
      const id = pageId !== null ? (declared ? `page:${pageId}` : null) : selectionAt(context0, target);
      if (id !== null && MOVABLE.test(id) && current.editable) current.begin({ kind: 'node', id }, event);
    };
    const onPointerUp = (event: PointerEvent): void => {
      if (off()) return;
      event.stopPropagation();
      if ((event.button ?? 0) !== 0 || latest.current.drag !== null || latest.current.edit !== null) return;
      // Un contrôle désactivé (lecture seule) ne reçoit pas toujours de clic : la sélection se fait ici.
      handled.current = true;
      latest.current.choose(event.target as Element, event.shiftKey);
    };
    const onClick = (event: MouseEvent): void => {
      if (off()) return;
      stop(event);
      if (handled.current) {
        handled.current = false;
        return;
      }
      if (latest.current.edit === null) latest.current.choose(event.target as Element, event.shiftKey);
    };
    const onDoubleClick = (event: MouseEvent): void => {
      if (off()) return;
      stop(event);
      const found = editableAt({ template, registry, host: element, page, pages }, event.target as Element);
      if (found !== null) latest.current.startEdit(found.id, found.part, found.element);
    };
    const onPointerOver = (event: PointerEvent): void => {
      if (off() || latest.current.drag !== null || latest.current.edit !== null) return;
      const id = selectionAt({ template, registry, host: element, page, pages }, event.target as Element);
      setHover((current) => (current === id ? current : id));
    };
    const onLeave = (): void => setHover(null);
    const onKey = (event: KeyboardEvent): void => {
      if (off()) return;
      // Pendant l'édition, aucune touche ne saisit ni n'active un contrôle de l'aperçu ; Tab reste.
      if (event.key === 'Tab' || event.ctrlKey || event.metaKey) return;
      event.stopPropagation();
      event.preventDefault();
    };
    const listeners: Array<[string, (event: never) => void]> = [
      ['pointerdown', onPointerDown],
      ['pointerup', onPointerUp],
      ['mousedown', stop],
      ['mouseup', stop],
      ['click', onClick],
      ['dblclick', onDoubleClick],
      ['pointerover', onPointerOver],
      ['keydown', onKey],
      ['keypress', onKey],
      ['beforeinput', stop],
      ['input', stop],
      ['change', stop],
      ['paste', stop],
      ['drop', stop],
      ['submit', stop],
      ['touchstart', (event: Event) => !off() && event.stopPropagation()],
    ];
    for (const [type, listener] of listeners) element.addEventListener(type, listener as EventListener, true);
    element.addEventListener('pointerleave', onLeave);
    return () => {
      for (const [type, listener] of listeners) element.removeEventListener(type, listener as EventListener, true);
      element.removeEventListener('pointerleave', onLeave);
    };
  }, [template, registry, page, pages, declared]);

  // Écouteurs de la coquille : glisser depuis la bibliothèque et les calques, Maj+clic, clavier.
  const shell = useRef({ startKeyboard, keyboardKey, toggleExtra, begin, library, selected, extras: shownExtras, edit, drag, actionsOf, template, editable });
  shell.current = { startKeyboard, keyboardKey, toggleExtra, begin, library, selected, extras: shownExtras, edit, drag, actionsOf, template, editable };
  useEffect(() => {
    if (root === null || trying) return undefined;
    const libraryEntry = (target: Element): LibraryEntry<Template> | undefined => {
      const id = target.closest('[data-library-entry]')?.getAttribute('data-library-entry');
      return id === null || id === undefined ? undefined : shell.current.library(shell.current.template).find((entry) => entry.id === id);
    };
    const layerNode = (target: Element): string | undefined => {
      const button = target.closest('.ay-studio-layers [data-layer]');
      return button?.closest('[data-node]')?.getAttribute('data-node') ?? undefined;
    };
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Element;
      const entry = libraryEntry(target);
      if (entry !== undefined) {
        shell.current.begin({ kind: 'library', entry }, event);
        return;
      }
      const node = layerNode(target);
      if (node !== undefined && MOVABLE.test(node)) shell.current.begin({ kind: 'node', id: node }, event);
    };
    const onClick = (event: MouseEvent): void => {
      if (!event.shiftKey) return;
      const node = layerNode(event.target as Element);
      if (node === undefined) return;
      event.preventDefault();
      event.stopPropagation();
      shell.current.toggleExtra(node);
    };
    let spaceTaken = false;
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as Element;
      const state = shell.current;
      if (target.closest('.ay-dnd-inline') !== null || state.edit !== null) return;
      if (state.drag?.mode === 'keyboard') {
        if (state.keyboardKey(event.key)) {
          event.preventDefault();
          event.stopPropagation();
          spaceTaken = event.key === ' ';
        }
        return;
      }
      const typing = target.matches('input:not([type=checkbox]):not([type=radio]):not([type=button]), textarea, select, [contenteditable="true"]');
      if (event.key === ' ' && !event.ctrlKey && !event.metaKey && !event.altKey && !event.shiftKey && !typing && state.editable) {
        const entry = libraryEntry(target);
        const node = layerNode(target);
        if (entry !== undefined || (node !== undefined && MOVABLE.test(node))) {
          event.preventDefault();
          event.stopPropagation();
          spaceTaken = true;
          state.startKeyboard(entry !== undefined ? { kind: 'library', entry } : { kind: 'node', id: node! }, false);
        }
        return;
      }
      if (typing || event.ctrlKey || event.metaKey || event.altKey) return;
      const open = document.querySelector('[role="dialog"], .ay-dnd-menu, .ay-studio__menu') !== null;
      if (event.key === 'Escape' && !open) {
        if (state.extras.length > 0) {
          event.preventDefault();
          event.stopPropagation();
          setExtras([]);
          return;
        }
        // Échap remonte au parent ; au formulaire, la coquille désélectionne puis ferme.
        const parent = state.selected === '' ? undefined : state.actionsOf(state.template, state.selected).parent;
        if (parent !== undefined) {
          event.preventDefault();
          event.stopPropagation();
          onSelect(parent);
        }
        return;
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && state.extras.length > 0 && !open) {
        event.preventDefault();
        event.stopPropagation();
        removeAllRef.current();
      }
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key === ' ' && spaceTaken) {
        event.preventDefault();
        event.stopPropagation();
        spaceTaken = false;
      }
    };
    const studio = root as HTMLElement;
    studio.addEventListener('pointerdown', onPointerDown, true);
    studio.addEventListener('click', onClick, true);
    studio.addEventListener('keydown', onKeyDown, true);
    studio.addEventListener('keyup', onKeyUp, true);
    return () => {
      studio.removeEventListener('pointerdown', onPointerDown, true);
      studio.removeEventListener('click', onClick, true);
      studio.removeEventListener('keydown', onKeyDown, true);
      studio.removeEventListener('keyup', onKeyUp, true);
    };
  }, [root, trying, onSelect]);

  // Mesures : après chaque rendu, au défilement, au redimensionnement, quand le rendu réel change.
  useEffect(() => {
    if (trying) return undefined;
    let frame = 0;
    const later = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setTick((tick) => tick + 1));
    };
    window.addEventListener('scroll', later, true);
    window.addEventListener('resize', later);
    const mutations = typeof MutationObserver === 'undefined' || host.current === null ? null : new MutationObserver(later);
    mutations?.observe(host.current!, { subtree: true, childList: true, characterData: true });
    const sizes = typeof ResizeObserver === 'undefined' || host.current === null ? null : new ResizeObserver(later);
    sizes?.observe(host.current!);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', later, true);
      window.removeEventListener('resize', later);
      mutations?.disconnect();
      sizes?.disconnect();
    };
  }, [trying]);
  useLayoutEffect(() => {
    const current = context();
    if (current === null || trying) return;
    const next: Record<string, Box> = {};
    for (const id of [selected, hover, ...shownExtras]) {
      if (id === null || id === '' || next[id] !== undefined) continue;
      const element = elementFor(current, id);
      if (element !== null) next[id] = boxOf(element);
    }
    // « ＋ Page » se pose juste après le dernier onglet ou la dernière étape, dans la barre elle-même.
    const tabs = current.host.querySelectorAll('.ay-tabs__tab, .ay-staged__step');
    const lastTab = tabs[tabs.length - 1];
    if (lastTab !== undefined) next['@bar'] = boxOf(lastTab);
    const stage = current.host.closest('.ay-studio__stage');
    if (stage !== null) next['@stage'] = boxOf(stage);
    const screen = current.host.closest('.ay-device__screen');
    if (screen !== null) next['@screen'] = boxOf(screen);
    if (drag?.mode === 'keyboard' && drag.target !== null) {
      const line = lineForTarget(current, drag.target);
      if (line !== null) next['@line'] = line;
    }
    const keys = new Set([...Object.keys(next), ...Object.keys(boxes)]);
    if ([...keys].some((key) => !sameBox(next[key], boxes[key]))) setBoxes(next);
  });

  // ---- Actions de la barre flottante.
  const actions = useMemo(() => (selected === '' ? {} : actionsOf(template, selected)), [actionsOf, template, selected]);
  const removeAll = (): void => {
    if (apply === undefined) return;
    const ids = [selected, ...shownExtras].filter((id) => id !== '' && id !== 'form');
    // Champs du dernier au premier (les chemins des précédents ne bougent pas), puis parties, blocs, pages.
    const rank = (id: string): number => (id.startsWith('field:') ? 0 : id.startsWith('block:') ? 1 : id.startsWith('section:') ? 2 : 3);
    const ordered = [...ids].sort((a, b) => rank(a) - rank(b) || b.localeCompare(a, undefined, { numeric: true }));
    let next = template;
    let removed = 0;
    for (const id of ordered) {
      const remove = actionsOf(next, id).remove;
      if (remove === undefined) continue;
      next = remove();
      removed += 1;
    }
    if (removed === 0) return;
    apply(next);
    setExtras([]);
    onSelect('');
    say(`${removed} élément${removed > 1 ? 's' : ''} supprimé${removed > 1 ? 's' : ''}. Ctrl Z pour annuler.`);
  };
  const removeAllRef = useRef(removeAll);
  removeAllRef.current = removeAll;

  const run = (change: (() => Template) | undefined, message: string, select?: string): void => {
    if (change === undefined || apply === undefined) return;
    apply(change());
    if (select !== undefined) onSelect(select);
    say(message);
  };
  const name = nameOf(selected);
  const isPage = selected.startsWith('page:');
  const toolbarActions: ToolbarAction[] = [];
  const menu: MenuAction[] = [];
  if (shownExtras.length > 0) {
    toolbarActions.push({ id: 'remove', label: 'Supprimer', icon: ICONS.remove, danger: true, keys: 'Suppr', onClick: removeAll });
    menu.push({ id: 'clear', label: 'Ne garder que le premier', onClick: () => setExtras([]) });
  } else if (selected !== '' && editable) {
    if (actions.parent !== undefined) toolbarActions.push({ id: 'parent', label: 'Choisir le parent', icon: ICONS.parent, keys: 'Échap', onClick: () => onSelect(actions.parent!) });
    if (!['form', 'header', 'footer', 'pages'].includes(selected)) {
      toolbarActions.push({ id: 'up', label: isPage ? 'Avancer' : 'Monter', icon: isPage ? ICONS.left : ICONS.up, keys: 'Alt ↑', disabled: actions.moveUp === undefined, onClick: () => run(actions.moveUp, `« ${name} » monté.`) });
      toolbarActions.push({ id: 'down', label: isPage ? 'Reculer' : 'Descendre', icon: isPage ? ICONS.right : ICONS.down, keys: 'Alt ↓', disabled: actions.moveDown === undefined, onClick: () => run(actions.moveDown, `« ${name} » descendu.`) });
      if (MOVABLE.test(selected)) toolbarActions.push({ id: 'move', label: 'Déplacer au clavier', icon: ICONS.move, onClick: () => startKeyboard({ kind: 'node', id: selected }, true) });
      if (onAdd !== undefined && actions.insertAfter !== undefined) {
        const target = actions.insertAfter;
        toolbarActions.push({ id: 'add', label: 'Ajouter ici', icon: ICONS.add, onClick: () => onAdd(target) });
      }
      if (actions.duplicate !== undefined) {
        const duplicate = actions.duplicate;
        toolbarActions.push({ id: 'duplicate', label: 'Dupliquer', icon: ICONS.duplicate, keys: 'Ctrl D', onClick: () => { const copy = duplicate(); apply!(copy.document); onSelect(copy.id); say('Copie ajoutée juste après.'); if (copy.id.startsWith('field:')) window.setTimeout(() => renameRef.current(copy.id), 0); } });
      }
      toolbarActions.push({ id: 'remove', label: 'Supprimer', icon: ICONS.remove, danger: true, keys: 'Suppr', disabled: actions.remove === undefined, onClick: () => run(actions.remove, `« ${name} » supprimé. Ctrl Z pour annuler.`, actions.parent ?? '') });
    }
    if (labelText(template, selected) !== '' && (selected === 'form' || MOVABLE.test(selected))) menu.push({ id: 'rename', label: 'Renommer', onClick: () => rename(selected) });
    for (const target of actions.moveTo ?? []) {
      menu.push({
        id: `to:${target.label}`,
        label: `Déplacer vers ${target.label.replace(/^Zone : /, 'la zone « ').replace(/^Page : /, 'la page « ')}${/^(Zone|Page) : /.test(target.label) ? ' »' : ''}`,
        onClick: () => {
          const result = target.apply();
          if (result !== null && typeof result === 'object' && 'refused' in (result as object)) {
            onRefused?.((result as { refused: string }).refused);
            say(`Refusé : ${(result as { refused: string }).refused}`);
          } else apply!(result as Template);
        },
      });
    }
    if (isPage) menu.push({ id: 'add-page', label: 'Ajouter une page après', onClick: () => addPage() });
  }
  const addPage = (): void => {
    if (apply === undefined) return;
    const result = addBlankPage(template, registry);
    if ('refused' in result) {
      onRefused?.(result.refused);
      say(`Refusé : ${result.refused}`);
      return;
    }
    apply(result.document);
    onSelect(result.id);
    onNavigate(result.id.slice(5));
    say('Page ajoutée avec une partie neuve. Double-cliquez son onglet pour la nommer.');
  };

  // ---- Calque.
  const frames: FrameSpec[] = [];
  const tag = (id: string): string => {
    const node = nodes.get(id);
    if (node === undefined) return '';
    // « Température relevée · Mesure » : le nom, puis la nature en clair ; ni glyphe ni coordonnée (addendum (15)).
    return `${node.label} · ${node.kind}`;
  };
  if (drag === null && edit === null && hover !== null && hover !== selected && !shownExtras.includes(hover) && boxes[hover] !== undefined) frames.push({ key: `hover:${hover}`, box: boxes[hover]!, tone: 'hover', label: tag(hover) });
  for (const id of shownExtras) if (boxes[id] !== undefined) frames.push({ key: `multi:${id}`, box: boxes[id]!, tone: 'multi', label: tag(id) });
  const resizable = editable && drag === null && edit === null && shownExtras.length === 0 && RESIZABLE.test(selected);
  if (selected !== '' && boxes[selected] !== undefined && drag?.mode !== 'pointer') {
    frames.push({
      key: `sel:${selected}`,
      box: boxes[selected]!,
      tone: 'selected',
      label: resize !== null ? `Largeur : ${resize.columns}/12 · Échap annule` : shownExtras.length > 0 ? `${shownExtras.length + 1} éléments · ${tag(selected)}` : tag(selected),
      ...(resizable ? { resize: { onStart: (side: 'e' | 'w', event: { clientX: number; preventDefault: () => void; stopPropagation: () => void }) => startResize(side, event) } } : {}),
    });
  }
  const anchor = boxes[selected];
  const toolbar = resize === null && drag === null && edit === null && selected !== '' && anchor !== undefined && editable && toolbarActions.length + menu.length > 0 ? { anchor, name: shownExtras.length > 0 ? `${shownExtras.length + 1} éléments` : name, actions: toolbarActions, menu } : undefined;
  const keyboardLine = boxes['@line'];
  const line = drag === null ? undefined : drag.mode === 'keyboard' ? (keyboardLine === undefined ? undefined : { box: keyboardLine, refused: drag.refused }) : drag.line === null ? undefined : { box: drag.line, refused: drag.refused };
  const bar = boxes['@bar'];
  const extra = (
    <>
      <div ref={sink} tabIndex={-1} className="ay-visually-hidden" aria-label="Placement en cours : flèches pour choisir, Espace pour poser, Échap pour annuler" />
      {resize !== null && (
        <>
          {Array.from({ length: 13 }, (_, index) => (
            <span key={index} className={`ay-dnd-guide${index === resize.columns ? ' is-active' : ''}`} style={{ left: resize.grid.left + (resize.grid.width / 12) * index, top: resize.grid.top, height: resize.grid.height }} aria-hidden="true" />
          ))}
          {boxes[resize.id] !== undefined && (
            <span className="ay-dnd-size" style={{ left: boxes[resize.id]!.left + boxes[resize.id]!.width / 2, top: boxes[resize.id]!.top }}>
              {resize.from}/12 → {resize.columns}/12
            </span>
          )}
        </>
      )}
      {editable && declared && bar !== undefined && drag === null && (
        <button type="button" className="ay-dnd-addpage" style={{ left: bar.left + bar.width + 8, top: bar.top + bar.height / 2 - 16 }} onClick={addPage} aria-label="Ajouter une page" title="Ajouter une page">＋ Page</button>
      )}
    </>
  );

  return (
    <>
      <div ref={host} className={`ay-forms-canvas${trying ? '' : ' is-editing'}`} data-zones={zones ? 'true' : undefined}>
        {children}
      </div>
      {!trying && (
        <InteractionLayer
          root={root}
          frames={frames}
          toolbar={toolbar}
          line={line}
          zones={drag?.mode === 'pointer' ? drag.zones.map((zone) => ({ key: zone.region, box: zone.box, label: zone.label, active: drag.target?.startsWith(`region:${zone.region}`) === true })) : []}
          dock={
            drag?.mode === 'pointer'
              ? { title: 'Déposer dans :', targets: drag.dock.map((entry) => ({ ...entry, active: drag.target === entry.target })) }
              : drag?.keyboard !== undefined
                ? {
                    title: `« ${drag.name} » :`,
                    targets: [
                      {
                        target: 'keyboard',
                        label: drag.keyboard.state.index < 0 ? 'flèches pour choisir sa place, Échap pour annuler' : `${drag.keyboard.slots[drag.keyboard.state.index]?.label ?? ''} · Espace pour poser`,
                        active: drag.keyboard.state.index >= 0 && drag.refused === null,
                        refused: drag.refused !== null,
                      },
                    ],
                  }
                : undefined
          }
          ghost={drag?.mode === 'pointer' && drag.point !== undefined ? { x: drag.point.x, y: drag.point.y, label: drag.refused === null ? drag.name : `${drag.name} · refusé`, refused: drag.refused !== null } : undefined}
          edit={
            edit === null
              ? undefined
              : {
                  id: `${edit.id}|${edit.part}`,
                  box: edit.box,
                  value: labelText(template, edit.id, edit.part),
                  label: `Libellé de « ${nameOf(edit.id)} »`,
                  ...(edit.fontSize === undefined ? {} : { fontSize: edit.fontSize }),
                  onCommit: (value) => {
                    setEdit(null);
                    focusStage();
                    const next = renameLabel(template, edit.id, value, edit.part);
                    if ('refused' in next) {
                      onRefused?.(next.refused);
                      say(`Refusé : ${next.refused}`);
                    } else if (next !== template && apply !== undefined) {
                      apply(next);
                      say(`Libellé modifié : « ${value.trim()} ». Ctrl Z pour annuler.`);
                    }
                  },
                  onCancel: () => {
                    setEdit(null);
                    focusStage();
                    say('Modification du libellé annulée.');
                  },
                }
          }
          live={live}
          extra={extra}
          clip={boxes['@stage']}
          screen={boxes['@screen']}
        />
      )}
    </>
  );
}
